import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useAdminClaimDetail from '../hook/useAdminClaimDetail.mjs';
import ClaimPtaeAllocation from '../../components/ClaimPtaeAllocation.mjs';
import { secureLogger } from '../../utils/secureLogger.mjs';
import PageShell from '../../components/PageShell.mjs';
import { useDialog } from '../../components/dialog/DialogProvider.mjs';
import { unpricedLinesWarning } from '../../expenseReimbursement/hook/useExpenseReimbursement.mjs';



const AdminClaimDetail = () => {
  const { claimType, claimId } = useParams();
  const navigate = useNavigate();
  const [actionError, setActionError] = useState(null);
  const { confirm } = useDialog();

  const {
    claim,
    loading,
    processing,
    error,
    generalComments,
    setGeneralComments,
    lineItemComments,
    updateLineItemComment,
    handleClaimAction,
    canProcessClaim
  } = useAdminClaimDetail(claimType, claimId);

  const handleAction = async (action) => {
    setActionError(null);
    // Same question the expense form asks: a non-USD line with no rate is not
    // refused any more, the server pays it 1:1, so the admin confirms that
    // knowingly. Asked before the request, so Cancel changes nothing.
    if (action === 'approved' && claimType === 'expense') {
      const warning = unpricedLinesWarning(claim?.items, 'Approve');
      if (warning && !await confirm(warning, {
        title: 'Lines without an exchange rate',
        confirmLabel: 'Approve',
      })) return;
    }
    try {
      await handleClaimAction(action);
      navigate('/admin/dashboard');
    } catch (error) {
      secureLogger.error('Claim action failed', { status: error?.status });
      setActionError('Action failed. Please try again.');
    }
  };

  const handleEditClaim = () => {
    // Navigate to the appropriate form page with admin context
    if (claimType === 'perdiem') {
      navigate(`/admin/perdiem/edit/${claimId}`);
    } else if (claimType === 'expense') {
      navigate(`/admin/expense/edit/${claimId}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'approved': return 'bg-status-approved-bg text-green-800';
      case 'rejected': return 'bg-status-rejected-bg text-red-800';
      case 'returned': return 'bg-status-returned-bg text-orange-800';
      case 'submitted': return 'bg-status-submitted-bg text-blue-800';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  // The route may arrive without a claim type (the loading and error states
  // render before one is known), so the band falls back to a generic title.
  const shellProps = {
    title: claimType
      ? `${claimType.charAt(0).toUpperCase() + claimType.slice(1)} Claim Review`
      : 'Claim Review',
    width: '5xl',
  };

  if (loading) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
          <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4"></div>
          <p className="text-gray-600">Loading claim details...</p>
        </div>
      </PageShell>
    );
  }

  if (error) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 w-full max-w-md mx-auto text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Claim</h3>
          <p className="text-gray-600 mb-4 break-words">{error}</p>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-6 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </PageShell>
    );
  }

  if (!claim) {
    return (
      <PageShell {...shellProps}>
        <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8 w-full max-w-md mx-auto text-center">
          <h3 className="text-xl font-semibold text-gray-800 mb-4">Claim not found</h3>
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-6 py-2 bg-stanford-red text-white rounded-lg hover:bg-stanford-light-red transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      {...shellProps}
      actions={
        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusBadgeClass(claim.status)}`}>
          {claim.status}
        </span>
      }
    >
        {actionError && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
            {actionError}
          </div>
        )}

        {/* PTAE allocation - required before the claim can be approved, and
            carried into the monthly report when it is generated. */}
        <ClaimPtaeAllocation
          claimType={claimType}
          claimId={claimId}
          claimTotal={claim.totalUsdAmount ?? claim.totalAmount ?? claim.totalClaim ?? 0}
          // Editable only while the claim is with the admin, as the server enforces.
          readOnly={!['submitted', 'returned', 'under_review'].includes(claim.status)}
        />

        {/* Basic Claim Information */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4">Claim Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm text-gray-500">Employee:</label>
              <p className="font-medium text-gray-800">{claim.user?.firstName} {claim.user?.lastName}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">Email:</label>
              <p className="font-medium text-gray-800 break-words">{claim.user?.email}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">Date Submitted:</label>
              <p className="font-medium text-gray-800">{formatDate(claim.createdAt)}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">Total Amount:</label>
              <p className="font-medium text-gray-800 tabular-nums">{formatCurrency(claim.totalAmount || claim.totalClaim)}</p>
            </div>
            {claimType === 'perdiem' && (
              <div className="md:col-span-2">
                <label className="text-sm text-gray-500">Reason for Trip:</label>
                <p className="font-medium text-gray-800 break-words">{claim.reasonForTrip}</p>
              </div>
            )}
          </div>
        </div>

        {/* Line Items Summary */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4">Line Items Summary</h3>

          {claimType === 'perdiem' && claim.dailyDetails && (
            <div className="space-y-2">
              <p><span className="text-gray-500">Number of days:</span> <span className="font-medium">{claim.dailyDetails.length}</span></p>
              <p><span className="text-gray-500">Date range:</span> <span className="font-medium">{formatDate(claim.dailyDetails[0]?.date)} - {formatDate(claim.dailyDetails[claim.dailyDetails.length - 1]?.date)}</span></p>
            </div>
          )}

          {claimType === 'expense' && claim.items && (
            <div className="space-y-2">
              <p><span className="text-gray-500">Number of expenses:</span> <span className="font-medium">{claim.items.length}</span></p>
              <p><span className="text-gray-500">Total amount:</span> <span className="font-medium tabular-nums">{formatCurrency(claim.totalAmount)}</span></p>
            </div>
          )}
        </div>

        {/* Admin Actions */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 mb-4 sm:mb-6">
          <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4">Admin Actions</h3>

          {/* Edit Button */}
          <div className="mb-6">
            <button
              onClick={handleEditClaim}
              disabled={processing}
              className="w-full sm:w-auto min-h-[44px] sm:min-h-0 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              Edit Claim Details
            </button>
            <p className="text-sm text-gray-500 mt-2">Click to open the full form for detailed editing</p>
          </div>

          {/* Line Item Comments for Return */}
          {claimType === 'perdiem' && claim.dailyDetails && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-3">Daily Comments (for returns):</h4>
              <div className="space-y-3">
                {claim.dailyDetails.map((day, index) => (
                  <div key={index}>
                    <label className="text-sm text-gray-600 mb-1 block">Day {index + 1} ({formatDate(day.date)}):</label>
                    <textarea
                      value={lineItemComments[index] || ''}
                      onChange={(e) => updateLineItemComment(index, e.target.value)}
                      placeholder="Add comments for this day..."
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {claimType === 'expense' && claim.items && (
            <div className="mb-6">
              <h4 className="font-medium text-gray-700 mb-3">Expense Comments (for returns):</h4>
              <div className="space-y-3">
                {claim.items.map((item, index) => (
                  <div key={index}>
                    <label className="text-sm text-gray-600 mb-1 block break-words">Expense {index + 1} ({item.expenditureDescription}):</label>
                    <textarea
                      value={lineItemComments[index] || ''}
                      onChange={(e) => updateLineItemComment(index, e.target.value)}
                      placeholder="Add comments for this expense..."
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* General Comments */}
          <div className="mb-6">
            <label className="font-medium text-gray-700 mb-2 block">General Comments:</label>
            <textarea
              value={generalComments}
              onChange={(e) => setGeneralComments(e.target.value)}
              placeholder="Add general comments about this claim..."
              rows="4"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-stanford-red focus:border-transparent"
            />
          </div>

          {/* Action Buttons */}
          {canProcessClaim && (
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <button
                onClick={() => handleAction('approved')}
                disabled={processing}
                className="min-h-[44px] sm:min-h-0 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Approve Claim'}
              </button>
              <button
                onClick={() => handleAction('returned')}
                disabled={processing}
                className="min-h-[44px] sm:min-h-0 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Return for Revision'}
              </button>
              <button
                onClick={() => handleAction('rejected')}
                disabled={processing}
                className="min-h-[44px] sm:min-h-0 px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {processing ? 'Processing...' : 'Reject Claim'}
              </button>
            </div>
          )}
        </div>

        {/* Edit History */}
        {claim.editHistory && claim.editHistory.length > 0 && (
          <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6">
            <h3 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4">Edit History</h3>
            <div className="space-y-4">
              {claim.editHistory.map((edit, index) => (
                <div key={index} className="border-l-4 border-stanford-red pl-4 py-2">
                  <div className="flex flex-wrap justify-between items-center gap-x-3 mb-2">
                    <span className="text-sm text-gray-500">{formatDate(edit.editedAt)}</span>
                    <span className="text-sm font-medium text-stanford-red">Edited by Admin</span>
                  </div>
                  {edit.changedFields && edit.changedFields.length > 0 && (
                    <div className="space-y-1">
                      <h5 className="text-sm font-medium text-gray-700">Changes Made:</h5>
                      {edit.changedFields.map((change, changeIndex) => (
                        <div key={changeIndex} className="text-sm break-words">
                          <strong className="text-gray-700">{change.field}:</strong>
                          <span className="text-red-500 line-through mx-2">{JSON.stringify(change.oldValue)}</span>
                          <span className="text-green-600">{JSON.stringify(change.newValue)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
    </PageShell>
  );
};

export default AdminClaimDetail;
